import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { Vehicle, PurchaseMethods, Quote, ModelYear } from '@sm360/widget-commons-vehicle';
import {
    AnalyticsService,
    ShiftDigitalService,
    TRACKING_TAGS as TAGS,
    ADOBE_TAGS,
    AdobeService,
} from '@sm360/widget-commons-general';

import PaymentCalculatorDisplay from 'payment-calculator/components/PaymentCalculatorDisplay/PaymentCalculatorDisplay.jsx';
import * as Actions from 'payment-calculator/actions/paymentCalculatorActions.js';
import 'payment-calculator/components/PaymentCalculator/paymentCalculator.scss';

const $window = WindowProxy.get();
class PaymentCalculator extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isPanelOpen: false,
        };

        this.handleFrequenciesPanel = this.handleFrequenciesPanel.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    handleFrequenciesPanel() {
        this.setState(
            (prevState) => {
                return { isPanelOpen: !prevState.isPanelOpen };
            },
            function() {
                if ($window) {
                    $window.document.body.classList.toggle('PaymentCalculator--open', this.state.isPanelOpen);
                }
            }
        );
    }

    handleClick(response) {
        if ($window) {
            const { quote, purchaseMethods, hasSDInitialClickSent } = this.props;
            // Only send initial click event once on page DR tool interaction
            if (!hasSDInitialClickSent) this.props.onSetSDInitialClickEvent();
            if (!this.state.isPanelOpen) {
                AnalyticsService.ga4Trigger(
                    TAGS.PAYMENT_CALCULATOR_CTA(this.props.modelYear.model, this.props.modelYear.year)
                );
                this.props.onOpen();
                $window.document.addEventListener('click', this.handleOutsideClick, false);

                if (quote)
                    ShiftDigitalService.updateDrCalculatorShown(
                        quote.id,
                        quote.vehicle,
                        purchaseMethods.getSelectedPurchaseMethod(),
                        this.props.modelYear
                    );
            } else {
                if (this.props.hasAdobeTagging) {
                    AdobeService.eventTrigger(
                        ADOBE_TAGS.SHOPPING_TOOLS_PAYMENT_ESTIMATOR(),
                        'payment-estimator-onclick'
                    );
                }

                this.props.onClose();
                $window.document.removeEventListener('click', this.handleOutsideClick, false);

                if (quote) {
                    ShiftDigitalService.updateDrCalculatorFinishForBuilder(
                        this.props.modelYear,
                        purchaseMethods.getSelectedPurchaseMethod()
                    );
                }
            }

            if (this.state.isPanelOpen && this.props.tradeInVehicle && this.props.tradeInVehicle.checkIsUpdatable()) {
                this.props.actions.saveTradeInVehicle(this.props.tradeInVehicle);
            }
            this.props.actions.selectPurchaseMethod(purchaseMethods.getSelectedPurchaseMethod(), !!response);
            this.handleFrequenciesPanel();
        }
    }

    handleOutsideClick(e) {
        if (this.node.contains(e.target)) {
            return;
        }

        this.handleClick();
    }

    render() {
        const frequenciesPanelClasses = ['frequencies-panel-wrapper', 'sr-paymentCalculator-Panelbackground'];

        if (this.state.isPanelOpen) {
            frequenciesPanelClasses.push('is-open');
        }

        const setPaymentLabel = !this.state.isPanelOpen
            ? 'PAYMENT_CALCULATOR.SET_PAYMENT'
            : 'PAYMENT_CALCULATOR.SAVE_PAYMENT';
        const iconPanel = !this.state.isPanelOpen ? 'icon-calculatrice' : 'icon-Crochet';

        const customButton = this.props.hasCustomButton && (
            <a onClick={this.handleClick} className="sr-button-2 is-fluid vehicle-cta-btn">
                <icon className={iconPanel} />
                {LocaleService.t(setPaymentLabel)}
            </a>
        );

        const wrapperClasses = ['payment-calculator-wrapper'];
        if (this.state.isPanelOpen) {
            wrapperClasses.push('is-open');
        }

        return (
            <div
                className={wrapperClasses.join(' ')}
                ref={(node) => {
                    this.node = node;
                }}
            >
                {customButton}
                <PaymentCalculatorDisplay
                    {...this.props}
                    panelFrequenciesClassNames={frequenciesPanelClasses}
                    handleFrequenciesPanel={this.handleClick}
                    frequenciesPanelOpen={this.state.isPanelOpen}
                    hasCustomButton={this.props.hasCustomButton}
                />
            </div>
        );
    }
}

PaymentCalculator.defaultProps = {
    purchaseMethods: new PurchaseMethods.PurchaseMethodsModel(),
    hasCustomButton: false,
    hasSDInitialClickSent: false,
    tradeInVehicle: null,
    onSetSDInitialClickEvent: noop,
    onClose: noop,
    onOpen: noop,
    hasAdobeTagging: false,
};

PaymentCalculator.propTypes = {
    purchaseMethods: PropTypes.instanceOf(PurchaseMethods.PurchaseMethodsModel),
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    hasCustomButton: PropTypes.bool,
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel),
    hasSDInitialClickSent: PropTypes.bool,
    onSetSDInitialClickEvent: PropTypes.func,
    actions: PropTypes.shape({
        saveTradeInVehicle: PropTypes.func.isRequired,
        selectPurchaseMethod: PropTypes.func.isRequired,
    }),
    quote: PropTypes.instanceOf(Quote.QuoteModel).isRequired,
    modelYear: PropTypes.instanceOf(ModelYear.ModelYearModel).isRequired,
    hasAdobeTagging: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        tradeInVehicle: state.tradeInVehicle,
        purchaseMethods: state.purchaseMethods,
        quote: state.quote,
        modelYear: state.modelYear,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Actions, dispatch),
    };
}

PaymentCalculator.displayName = 'PaymentCalculator';

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PaymentCalculator);
export { PaymentCalculator };
